import React from 'react';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';

import InventoryCount from './InventoryCount';

import { ModelImage } from '../ModelImage';
import { getModelTitle } from '../../utils/getModelTitle';
import { getFormattedPrice } from '../../utils/getFormattedPrice';

const GridCard = ({ modelObject, modelLink }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const { locale, widgetName, windowId } = useRequestData();

	const { image, modelInv } = modelObject;
	const {
		showInventoryOnLotCount,
		modelTitleAttributes,
		startingAtPriceLabel,
		showPricing,
		includeDestinationChargeInPrice,
		isFragmentPage
	} = prefs;

	const showInventoryCount = showInventoryOnLotCount === 'true';
	const modelClassNames = [
		'model',
		showInventoryCount ? 'showing-count' : null,
		isFragmentPage === 'true' ? 'p-1' : 'p-6'
	];
	const cardClassNames = [
		'model-card',
		'box',
		isFragmentPage !== 'true' ? 'box-border' : 'border-0 bg-transparent',
		'd-flex',
		'flex-column',
		'position-relative'
	];
	const modelTitleClassName = [
		isFragmentPage === 'true' ? 'font-weight-base' : 'ddc-font-size-large',
		'font-weight-bold',
		'justify-content-start',
		'd-flex'
	];
	const priceClassNames = [
		'price',
		'ddc-font-size-small',
		'inline-block',
		'justify-content-start',
		'd-flex',
		isFragmentPage === 'true' ? 'fragment-page-price' : null
	];
	const modelTitle = getModelTitle(modelTitleAttributes, modelObject);
	const formattedPrice = getFormattedPrice(
		locale,
		includeDestinationChargeInPrice === 'true',
		modelObject
	);

	const trackClick = () => {
		trackEvent(widgetName, windowId, {
			action: 'clicked',
			element: 'Model Image',
			elementCTA: modelTitle,
			result: 'Navigated to model listings'
		});
	};

	return (
		<div className={setClassNames(cardClassNames)}>
			<div className={setClassNames(modelClassNames)}>
				<div className=" model-card-grid-title-container d-flex flex-column">
					<span className={setClassNames(modelTitleClassName)}>
						{modelTitle}
					</span>
					{showPricing === 'true' && (
						<span className={setClassNames(priceClassNames)}>
							{`${labels.get(startingAtPriceLabel)} `}{' '}
							{formattedPrice}
						</span>
					)}
				</div>

				<a
					href={modelLink}
					onClick={trackClick}
					className={`image-container ${
						isFragmentPage === 'true' ? 'py-3' : 'py-9'
					}`}
					data-testid="model-card-grid-component-test"
				>
					<ModelImage url={image} altImage={modelTitle} />
				</a>
				<InventoryCount model={modelInv} />
			</div>
		</div>
	);
};

GridCard.propTypes = {
	modelObject: PropTypes.shape({
		year: PropTypes.number,
		make: PropTypes.string,
		model: PropTypes.string,
		modelInv: PropTypes.string,
		image: PropTypes.string,
		inventoryCount: PropTypes.number,
		minStartingAtPrice: PropTypes.number
	}).isRequired,
	modelLink: PropTypes.string.isRequired
};

export default GridCard;
